<template>
  <div
    v-if="displayStyle === 'cimStyle'"
    @click="emit('folderClick', folderModel)"
  >
    <div class="rounded-lg border bg-white shadow-md transition duration-200 ease-in-out transform hover:shadow-lg hover:scale-105 p-4">
      <div class="flex items-center justify-between gap-x-4">
        <div class="flex items-center gap-x-4 flex-1 min-w-0">
          <div class="flex items-center flex-shrink-0">
            <Icon
              name="material-symbols:folder"
              class="text-primary-500 h-12 w-12"
            />
          </div>
          <div class="flex flex-col flex-1 min-w-0">
            <span
              class="font-bold break-words text-base"
              :title="folderModel.name"
            >{{ folderModel.name }}</span>
            <span
              v-if="folderModel.createdAt"
              class="text-sm text-gray-400 mt-1"
            >
              {{ getSubfolderCount(folderModel) }} subfolder{{ getSubfolderCount(folderModel) !== 1 ? 's' : '' }}, {{ folderModel?.Files?.length || 0 }} file{{ (folderModel?.Files?.length || 0) !== 1 ? 's' : '' }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div
    v-else
    class="rounded-lg border bg-white shadow-md transition duration-200 ease-in-out transform hover:shadow-lg hover:scale-105 cursor-pointer"
    :class="displayStyle === 'slim' ? 'p-2' : 'p-4'"
    @click="emit('folderClick', folderModel)"
  >
    <div class="flex gap-x-1">
      <div class="flex items-center">
        <Icon
          name="material-symbols:folder"
          class="text-primary-500"
          :class="displayStyle === 'slim' ? 'h-6 w-6' : 'h-12 w-12'"
        />
      </div>
      <div
        class="grid grid-cols-1 flex-1 items-center gap-1"
        :class="displayStyle === 'slim' ? 'px-2' : 'px-5'"
      >
        <span
          :class="displayStyle === 'slim' ? 'text-sm' : 'text-base'"
          class="font-semibold break-words"
          :title="folderModel.name"
        >{{ folderModel.name }}</span>
        <span
          :class="displayStyle === 'slim' ? 'text-xs' : 'text-sm'"
          class="text-gray-500"
        >
          {{ getSubfolderCount(folderModel) }} subfolder{{ getSubfolderCount(folderModel) !== 1 ? 's' : '' }}, {{ folderModel?.Files?.length || 0 }} file{{ (folderModel?.Files?.length || 0) !== 1 ? 's' : '' }}
        </span>
        <span
          v-if="showCreatedAt && folderModel.createdAt"
          :class="displayStyle === 'slim' ? 'text-xs' : 'text-sm'"
          class="text-gray-500"
        >Created at: {{ formatDate(folderModel.createdAt, TimeStampFormat.DayMonthYear) }}</span>
        <span
          v-if="folderModel.isSharedToVdr"
          class="inline-flex w-fit items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700"
        >
          <Icon
            name="material-symbols:lock-outline"
            class="h-3 w-3"
          />
          In Data Room
        </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Folder } from '~/types/folders'

const emit = defineEmits(['folderClick', 'preview', 'select'])

const displayStyle = defineModel<string>('displayStyle', { required: true, default: 'default' })

const getSubfolderCount = (folder: Folder) => {
  if (!folder) {
    return 0
  }

  if (folder?.childFolders && folder?.childFolders?.length > 0) {
    return folder?.childFolders?.length || 0
  }

  return 0
}

interface Props {
  businessId: string
  showCreatedAt?: boolean
}

withDefaults(defineProps<Props>(), {
  showCreatedAt: false,
})

const folderModel = defineModel<Folder>({ required: true })

const { formatDate, TimeStampFormat } = useUtils()
</script>
